import React, { useMemo, useState } from "react";

export default function FiltersModal({
  ui,
  categories,
  departments,
  draft,
  setDraft,
  onClose,
  onApply,
  onClear,
}) {
  const [q, setQ] = useState("");

  const formatLabel = (v = "") =>
    v
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const normalizeCategory = (v = "") =>
    v
      .replace(/_/g, " ")
      .replace(/\s*-\s*/g, " - ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  const normalizeDepartment = (v = "") => v.replace(/\s+/g, " ").trim().toLowerCase();

  const categoryGroups = useMemo(() => {
    const map = new Map();

    categories.forEach((c) => {
      const key = normalizeCategory(c);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });

    return Array.from(map.entries()).map(([key, values]) => ({
      key,
      label: formatLabel(values[0]),
      values,
    }));
  }, [categories]);

  const departmentGroups = useMemo(() => {
    const map = new Map();

    departments.forEach((d) => {
      const key = normalizeDepartment(d);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    });

    return Array.from(map.entries()).map(([key, values]) => ({
      key,
      label: formatLabel(values[0]),
      values,
    }));
  }, [departments]);

  const filteredCategories = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return categoryGroups;
    return categoryGroups.filter((g) => g.label.toLowerCase().includes(s));
  }, [categoryGroups, q]);

  const filteredDepartments = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return departmentGroups;
    return departmentGroups.filter((g) => g.label.toLowerCase().includes(s));
  }, [departmentGroups, q]);

  return (
    <div className="bi-modalOverlay" role="dialog" aria-modal="true">
      <div className="bi-modal">
        <div className="bi-modalHead">
          <div className="bi-modalTitle">{ui.filtersTitle}</div>

          <div className="bi-modalHeadRight">
            <div className="bi-search" style={{ width: 360, maxWidth: "50vw" }}>
              <span className="bi-searchIcon">⌕</span>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={ui.filtersSearchPlaceholder}
              />
            </div>

            <button className="bi-btn is-outline" type="button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        <div className="bi-modalBody">
          <div>
            <div className="bi-filterGroupTitle">{ui.categoryLabel}</div>
            {filteredCategories.map((g) => {
              const checked = g.values.every((v) => draft.categories.has(v));

              return (
                <label key={g.key} className="bi-checkRow">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const next = new Set(draft.categories);
                      if (e.target.checked) {
                        g.values.forEach((v) => next.add(v));
                      } else {
                        g.values.forEach((v) => next.delete(v));
                      }
                      setDraft({ ...draft, categories: next });
                    }}
                  />
                  <span>{g.label}</span>
                </label>
              );
            })}
          </div>

          <div>
            <div className="bi-filterGroupTitle">{ui.departmentLabel}</div>
            {filteredDepartments.map((g) => {
              const checked = g.values.every((v) => draft.departments.has(v));

              return (
                <label key={g.key} className="bi-checkRow">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const next = new Set(draft.departments);
                      if (e.target.checked) {
                        g.values.forEach((v) => next.add(v));
                      } else {
                        g.values.forEach((v) => next.delete(v));
                      }
                      setDraft({ ...draft, departments: next });
                    }}
                  />
                  <span>{g.label}</span>
                </label>
              );
            })}
          </div>

          <div className="bi-dateRow">
            <div className="bi-filterGroupTitle">{ui.creationDateLabel}</div>
            <div className="bi-dateGrid">
              <input
                type="date"
                value={draft.startDate}
                onChange={(e) => setDraft({ ...draft, startDate: e.target.value })}
                placeholder={ui.startDatePlaceholder}
              />
              <input
                type="date"
                value={draft.endDate}
                onChange={(e) => setDraft({ ...draft, endDate: e.target.value })}
                placeholder={ui.endDatePlaceholder}
              />
            </div>
          </div>
        </div>

        <div className="bi-modalFoot">
          <button type="button" className="bi-advBtn" onClick={onClear}>
            {ui.clearLabel}
          </button>

          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" className="bi-btn is-outline" onClick={onClose}>
              {ui.cancelLabel}
            </button>
            <button type="button" className="bi-btn is-solid" onClick={onApply}>
              {ui.applyLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
